import { useState, useMemo, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, Loader2, Bell, BellOff, AlertTriangle, Receipt, Settings2, Play, Check } from "lucide-react";
import { getOrders, setOrderStatus, getMenuMeta, getOutlet, errMsg } from "@/lib/api";
import {
  playChime,
  requestNotify,
  notify,
  startRinging,
  stopRinging,
  previewSound,
  getAlertSound,
  setAlertSound,
  ALERT_SOUNDS,
} from "@/lib/sound";
import { useAuth } from "@/lib/auth";
import {
  inr,
  shortId,
  timeAgo,
  bucketOf,
  itemCount,
  summarizeOrders,
  BUCKETS,
  BUCKET_LABELS,
} from "@/lib/format";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import OrderDetail from "@/components/OrderDetail";
import ThermalReceipt from "@/components/ThermalReceipt";
import KotTicket from "@/components/KotTicket";
import DaySlip from "@/components/DaySlip";
import { Countdown } from "@/components/Countdown";

const badgeMini = {
  new: "bg-amber-500",
  preparing: "bg-blue-500",
  ready: "bg-violet-500",
  completed: "bg-emerald-500",
  cancelled: "bg-red-500",
};

function OrderCard({ order, active, onClick }) {
  const bucket = bucketOf(order);
  const isNew = bucket === "new";
  const summary = (order.items || []).map((i) => `${i.quantity || 1}× ${i.name}`).join(", ");
  return (
    <button
      onClick={onClick}
      data-testid={`order-card-${order.id}`}
      className={cn(
        "w-full text-left rounded-md border p-3.5 transition-colors relative",
        active ? "border-primary bg-accent" : "border-border bg-card hover:bg-muted/60",
        isNew && !active && "border-amber-400 animate-glow"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="font-display font-bold text-base">{shortId(order.id)}</span>
        <span className="text-xs text-muted-foreground">{timeAgo(order.created_at)}</span>
      </div>
      <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{summary || "—"}</p>
      {bucket === "preparing" ? (
        <div className="mt-1.5">
          <Countdown order={order} compact />
        </div>
      ) : null}
      <div className="flex items-center justify-between mt-2.5">
        <span className="text-xs text-muted-foreground">{itemCount(order)} item(s)</span>
        <span className="font-mono font-semibold text-sm">{inr(order.total ?? order.gross_total)}</span>
      </div>
      <span className={cn("absolute left-0 top-3 bottom-3 w-1 rounded-full", badgeMini[bucket])} />
    </button>
  );
}

export default function Orders() {
  const { vendor } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("new");
  const [selectedId, setSelectedId] = useState(null);
  const [printOrder, setPrintOrder] = useState(null);
  const [mobileDetail, setMobileDetail] = useState(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("vendor_sound") === "1");
  const [slipOpen, setSlipOpen] = useState(false);
  const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const [slipFrom, setSlipFrom] = useState(todayStr);
  const [slipTo, setSlipTo] = useState(todayStr);
  const [slipRange, setSlipRange] = useState(null);
  const [soundPickerOpen, setSoundPickerOpen] = useState(false);
  const [alertSound, setAlertSoundState] = useState(getAlertSound);
  const prevNewCount = useRef(null);
  const overdueAlerted = useRef(new Set());

  const chooseSound = (key) => {
    setAlertSound(key);
    setAlertSoundState(key);
    previewSound(key);
  };

  const toggleSound = async () => {
    const next = !soundOn;
    setSoundOn(next);
    localStorage.setItem("vendor_sound", next ? "1" : "0");
    if (next) {
      const perm = await requestNotify();
      playChime();
      toast.success(perm === "granted" ? "Alerts on — sound + notifications" : "Order sound on");
    } else {
      toast.message("Order alerts off");
    }
  };

  const { data, isLoading, isError, error, refetch, isRefetching } = useQuery({
    queryKey: ["vendor-orders"],
    queryFn: async () => (await getOrders({ limit: 200 })).data,
    refetchInterval: 12000,
    refetchIntervalInBackground: true,
  });

  const orders = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return [...list].sort(
      (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
    );
  }, [data]);

  // Map menu item id -> sub-category, so the KOT can print it for the kitchen.
  const { data: metaData } = useQuery({ queryKey: ["menu-meta"], queryFn: async () => (await getMenuMeta()).data });
  const { data: outlet } = useQuery({ queryKey: ["outlet"], queryFn: async () => (await getOutlet()).data, staleTime: 300000 });
  const subMap = useMemo(() => {
    const m = {};
    for (const r of Array.isArray(metaData) ? metaData : []) {
      if (r.subcategory) m[r.item_id] = r.subcategory;
    }
    return m;
  }, [metaData]);

  const grouped = useMemo(() => {
    const g = { new: [], preparing: [], ready: [], completed: [], cancelled: [] };
    for (const o of orders) g[bucketOf(o)].push(o);
    return g;
  }, [orders]);

  const parseDay = (str, end) => {
    const [y, m, d] = str.split("-").map(Number);
    return end
      ? new Date(y, m - 1, d, 23, 59, 59, 999)
      : new Date(y, m - 1, d, 0, 0, 0, 0);
  };

  // Live on-screen summary for the picked range (so the vendor sees it before printing).
  const slipPreview = useMemo(() => {
    if (!slipFrom || !slipTo) return null;
    return summarizeOrders(orders, parseDay(slipFrom, false), parseDay(slipTo, true));
  }, [orders, slipFrom, slipTo]);

  // New order alert — ring the sweet chime repeatedly for up to 5 min (or until
  // the vendor accepts/rejects, which clears the "new" bucket and stops it).
  useEffect(() => {
    const n = grouped.new.length;
    const increased = prevNewCount.current != null && n > prevNewCount.current;
    if (increased) {
      const delta = n - prevNewCount.current;
      toast.info(`${delta} new order(s) received`, { icon: <Bell className="h-4 w-4" /> });
      notify("New order · 1APPONLY", `${delta} new order(s) just landed`);
    }
    prevNewCount.current = n;

    if (!soundOn || n === 0) {
      stopRinging();
    } else if (increased) {
      stopRinging();      // reset the 5-min window when a fresh order arrives
      startRinging();
    } else {
      startRinging();     // no-op if already ringing
    }
  }, [grouped.new.length, soundOn]);

  // Stop any ringing when leaving the screen.
  useEffect(() => () => stopRinging(), []);

  // Overdue alert: flash + chime when an accepted order's prep time runs out.
  useEffect(() => {
    const check = () => {
      const now = Date.now();
      for (const o of grouped.preparing) {
        if (!o.accepted_at || !o.prep_time_min) continue;
        const deadline = new Date(o.accepted_at).getTime() + o.prep_time_min * 60000;
        if (now > deadline && !overdueAlerted.current.has(o.id)) {
          overdueAlerted.current.add(o.id);
          toast.error(`${shortId(o.id)} is overdue — prep time is up!`, {
            icon: <AlertTriangle className="h-4 w-4" />,
            duration: 6000,
          });
          if (soundOn) playChime();
          notify("Order overdue · 1APPONLY", `${shortId(o.id)} prep time is up`);
        }
      }
    };
    check();
    const id = setInterval(check, 1000);
    return () => clearInterval(id);
  }, [grouped.preparing, soundOn]);

  const list = grouped[tab] || [];
  const selected = orders.find((o) => o.id === selectedId) || null;

  const doPrint = (kind, order) => {
    if ((kind === "bill" || kind === "kot") && !order) return;
    if (order) setPrintOrder(order);
    requestAnimationFrame(() => {
      const map = { bill: "receipt", kot: "kot", day: "dayslip" };
      ["receipt", "kot", "dayslip"].forEach((eid) => {
        const el = document.getElementById(eid);
        if (el) el.classList.toggle("print-active", map[kind] === eid);
      });
      setTimeout(() => window.print(), 90);
    });
  };

  const printDaySummary = () => doPrint("day");

  const applyPreset = (preset) => {
    const now = new Date();
    const iso = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    if (preset === "today") {
      setSlipFrom(iso(now)); setSlipTo(iso(now));
    } else if (preset === "yesterday") {
      const y = new Date(now); y.setDate(now.getDate() - 1);
      setSlipFrom(iso(y)); setSlipTo(iso(y));
    } else if (preset === "7days") {
      const s = new Date(now); s.setDate(now.getDate() - 6);
      setSlipFrom(iso(s)); setSlipTo(iso(now));
    } else if (preset === "week") {
      const s = new Date(now);
      const dow = (now.getDay() + 6) % 7; // Monday = 0
      s.setDate(now.getDate() - dow);
      setSlipFrom(iso(s)); setSlipTo(iso(now));
    }
  };

  const printRangeSummary = () => {
    const from = parseDay(slipFrom, false);
    const to = parseDay(slipTo, true);
    const sameDay = from.toDateString() === to.toDateString();
    const label = sameDay
      ? from.toDateString() === new Date().toDateString() ? "Today" : "Single day"
      : "Date range";
    setSlipRange({ from, to, label });
    setSlipOpen(false);
    requestAnimationFrame(() => printDaySummary());
  };

  const mutation = useMutation({
    mutationFn: ({ id, body }) => setOrderStatus(id, body),
    onSuccess: (_res, vars) => {
      toast.success("Order updated");
      if (vars?.body?.status === "accepted") {
        const ord = orders.find((o) => o.id === vars.id) || selected;
        if (ord) {
          toast.info("Printing kitchen ticket (KOT)…");
          doPrint("kot", { ...ord, prep_time_min: vars.body.prep_time_min || ord.prep_time_min });
        }
      }
      qc.invalidateQueries({ queryKey: ["vendor-orders"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const pickOrder = (o) => {
    setSelectedId(o.id);
    if (window.innerWidth < 1024) setMobileDetail(true);
  };

  const handlePrint = (o) => doPrint("bill", o);

  const detailProps = {
    order: selected,
    busy: mutation.isPending,
    onAccept: (prep) =>
      mutation.mutate({ id: selected.id, body: { status: "accepted", prep_time_min: prep } }),
    onReject: (reason) =>
      mutation.mutate({ id: selected.id, body: { status: "rejected", reason: reason || "Rejected by vendor" } }),
    onAdvance: (status) => mutation.mutate({ id: selected.id, body: { status } }),
    onPrint: () => handlePrint(selected),
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen flex flex-col">
      {/* Top header */}
      <div className="px-5 sm:px-6 py-4 border-b border-border flex items-center justify-between gap-4 bg-card">
        <div>
          <h1 className="font-display font-black text-2xl leading-none">Live Orders</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {grouped.new.length > 0 ? (
              <span className="text-amber-600 font-semibold">{grouped.new.length} awaiting action</span>
            ) : (
              "All caught up"
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setSlipOpen(true)}
            data-testid="print-day-summary-btn"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md border border-border px-3 py-2"
          >
            <Receipt className="h-4 w-4" />
            <span className="hidden sm:inline">Sales summary</span>
          </button>
          <button
            onClick={toggleSound}
            data-testid="sound-toggle-btn"
            className={cn(
              "inline-flex items-center gap-2 text-sm rounded-md border px-3 py-2 transition-colors",
              soundOn ? "border-primary/40 bg-accent text-primary" : "border-border text-muted-foreground hover:text-foreground"
            )}
          >
            {soundOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            <span className="hidden sm:inline">{soundOn ? "Alerts on" : "Alerts off"}</span>
          </button>
          <button
            onClick={() => setSoundPickerOpen(true)}
            data-testid="sound-settings-btn"
            title="Change alert sound"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md border border-border px-3 py-2"
          >
            <Settings2 className="h-4 w-4" />
            <span className="hidden sm:inline">Sound</span>
          </button>
          <button
            onClick={() => refetch()}
            data-testid="refresh-orders-btn"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors rounded-md border border-border px-3 py-2"
          >
            <RefreshCw className={cn("h-4 w-4", isRefetching && "animate-spin")} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-3 sm:px-4 border-b border-border bg-card overflow-x-auto">
        <div className="flex gap-1 min-w-max">
          {BUCKETS.map((b) => (
            <button
              key={b}
              onClick={() => setTab(b)}
              data-testid={`tab-${b}`}
              className={cn(
                "px-3.5 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2",
                tab === b
                  ? "border-primary text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              )}
            >
              {BUCKET_LABELS[b]}
              {grouped[b].length > 0 && (
                <span
                  className={cn(
                    "text-xs px-1.5 py-0.5 rounded-full font-semibold",
                    b === "new" ? "bg-amber-500 text-white" : "bg-muted text-muted-foreground"
                  )}
                >
                  {grouped[b].length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Body: two-pane */}
      <div className="flex-1 min-h-0 grid lg:grid-cols-[380px_1fr]">
        {/* List */}
        <div className="border-r border-border overflow-y-auto p-3 space-y-2.5">
          {isLoading ? (
            <div className="flex items-center justify-center py-20 text-muted-foreground">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : isError ? (
            <div className="text-center py-16 text-sm text-destructive">{errMsg(error)}</div>
          ) : list.length === 0 ? (
            <div className="text-center py-16 text-sm text-muted-foreground">
              No {BUCKET_LABELS[tab].toLowerCase()} orders.
            </div>
          ) : (
            list.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                active={o.id === selectedId}
                onClick={() => pickOrder(o)}
              />
            ))
          )}
        </div>

        {/* Detail (desktop) */}
        <div className="hidden lg:block overflow-hidden bg-card">
          <OrderDetail {...detailProps} />
        </div>
      </div>

      {/* Detail (mobile sheet) */}
      <Sheet open={mobileDetail} onOpenChange={setMobileDetail}>
        <SheetContent side="bottom" className="h-[94vh] p-0 rounded-t-xl lg:hidden">
          <div className="h-full">
            <OrderDetail {...detailProps} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Hidden thermal docs for printing */}
      <ThermalReceipt order={printOrder || selected} vendor={vendor} outlet={outlet} />
      <KotTicket order={printOrder || selected} vendor={vendor} subMap={subMap} outlet={outlet} />
      <DaySlip orders={orders} vendor={vendor} range={slipRange} />

      {/* Alert sound picker */}
      <Dialog open={soundPickerOpen} onOpenChange={setSoundPickerOpen}>
        <DialogContent data-testid="sound-picker-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Alert sound</DialogTitle>
            <DialogDescription>Pick the sound played for new-order alerts.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {ALERT_SOUNDS.map((s) => {
              const active = alertSound === s.key;
              return (
                <div
                  key={s.key}
                  className={cn(
                    "flex items-center justify-between rounded-md border px-3.5 py-3 transition-colors",
                    active ? "border-primary bg-accent" : "border-border hover:bg-muted/60"
                  )}
                >
                  <button
                    onClick={() => chooseSound(s.key)}
                    data-testid={`sound-option-${s.key}`}
                    className="flex items-center gap-2.5 flex-1 text-left"
                  >
                    <span
                      className={cn(
                        "h-5 w-5 rounded-full border flex items-center justify-center shrink-0",
                        active ? "border-primary bg-primary text-primary-foreground" : "border-muted-foreground/40"
                      )}
                    >
                      {active && <Check className="h-3.5 w-3.5" />}
                    </span>
                    <span className="font-medium text-sm">{s.label}</span>
                  </button>
                  <button
                    onClick={() => previewSound(s.key)}
                    data-testid={`sound-preview-${s.key}`}
                    title="Preview"
                    className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors rounded-md border border-border px-2.5 py-1.5"
                  >
                    <Play className="h-3.5 w-3.5" /> Play
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-muted-foreground">
            The selected sound is used for new-order alerts and repeats for up to 5 minutes until you accept or reject.
          </p>
          <DialogFooter>
            <Button onClick={() => setSoundPickerOpen(false)} data-testid="sound-picker-done">
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sales summary range picker */}
      <Dialog open={slipOpen} onOpenChange={setSlipOpen}>
        <DialogContent data-testid="sales-summary-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Sales summary</DialogTitle>
            <DialogDescription>Review sales for a day or range, then print.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {[
                ["today", "Today"],
                ["yesterday", "Yesterday"],
                ["7days", "Last 7 days"],
                ["week", "This week"],
              ].map(([k, lbl]) => (
                <button
                  key={k}
                  onClick={() => applyPreset(k)}
                  data-testid={`slip-preset-${k}`}
                  className="text-sm rounded-full border border-border px-3 py-1.5 hover:bg-accent hover:text-primary transition-colors"
                >
                  {lbl}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>From</Label>
                <Input
                  type="date"
                  value={slipFrom}
                  max={slipTo}
                  onChange={(e) => setSlipFrom(e.target.value)}
                  data-testid="slip-from"
                />
              </div>
              <div className="space-y-1.5">
                <Label>To</Label>
                <Input
                  type="date"
                  value={slipTo}
                  min={slipFrom}
                  onChange={(e) => setSlipTo(e.target.value)}
                  data-testid="slip-to"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Totals every non-cancelled order in the selected period — order count, items sold and total sales.
            </p>

            {/* On-screen preview — review before printing */}
            <div className="rounded-md border border-border bg-muted/40 p-3.5" data-testid="slip-preview">
              <div className="flex items-center justify-between text-sm font-semibold">
                <span>Orders</span>
                <span data-testid="slip-preview-orders">{slipPreview?.count ?? 0}</span>
              </div>
              <div className="flex items-center justify-between text-sm font-semibold mt-1">
                <span>Items sold</span>
                <span data-testid="slip-preview-items">{slipPreview?.totalItems ?? 0}</span>
              </div>
              <div className="mt-2.5 max-h-44 overflow-y-auto">
                {slipPreview && slipPreview.rows.length > 0 ? (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-xs text-muted-foreground">
                        <th className="text-left font-medium py-1">Item</th>
                        <th className="text-center font-medium py-1">Qty</th>
                        <th className="text-right font-medium py-1">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {slipPreview.rows.map(([name, v]) => (
                        <tr key={name} className="border-b border-border/50 last:border-0">
                          <td className="py-1.5 pr-2">{name}</td>
                          <td className="py-1.5 text-center tabular-nums">{v.qty}</td>
                          <td className="py-1.5 text-right font-mono">{inr(v.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-muted-foreground py-2 text-center">No sales in this period.</p>
                )}
              </div>
              <div className="flex items-center justify-between border-t border-border mt-2 pt-2 font-black text-lg">
                <span>Total</span>
                <span className="font-mono" data-testid="slip-preview-total">{inr(slipPreview?.sales ?? 0)}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlipOpen(false)} data-testid="slip-cancel">
              Cancel
            </Button>
            <Button onClick={printRangeSummary} data-testid="slip-print" disabled={!slipPreview || slipPreview.count === 0}>
              <Receipt className="h-4 w-4 mr-1.5" /> Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
